import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../config/api';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  BellIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

import ContactDetailModal from './ContactDetailModal';
import ContactEditModal from './ContactEditModal';

interface Contact {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string;
  business_type: string;
  business_type_display: string;
  message?: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  assigned_to?: string;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  first_contact_date?: string;
  last_contact_date?: string;
  next_followup_date?: string;
}

interface ContactListResponse {
  contacts: Contact[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

const ContactManagement: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // 필터링 및 검색 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // 통계 정보
  const [stats, setStats] = useState({
    total_contacts: 0,
    new_contacts: 0,
    in_progress_contacts: 0,
    completed_contacts: 0,
    today_contacts: 0,
    this_week_contacts: 0,
    this_month_contacts: 0,
  });

  // WebSocket 제거: 페이지 새로고침 시 최신 데이터 로드
  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [
    currentPage,
    searchTerm,
    statusFilter,
    priorityFilter,
    businessTypeFilter,
  ]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(priorityFilter && { priority: priorityFilter }),
        ...(businessTypeFilter && { business_type: businessTypeFilter }),
      });

      const response = await fetch(
        `${API_BASE_URL}/v1/contacts?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      if (!response.ok) throw new Error('문의 목록 조회 실패');

      const data: ContactListResponse = await response.json();
      setContacts(data.contacts);
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/contacts/stats/summary`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('통계 조회 실패:', err);
    }
  };

  // setupWebSocket 함수 제거: 페이지 새로고침으로 대체

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact);
    setShowDetailModal(true);
    // 새 알림 카운트 초기화 (해당 문의를 확인했으므로)
    // TODO: 알림 카운트 관리 필요 시 상위 컴포넌트에서 관리
    // if (contact.status === 'new') {
    //   setNewContactNotifications((prev) => Math.max(0, prev - 1));
    // }
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  const getStatusBadgeColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      new: 'bg-blue-100 text-blue-800',
      reviewing: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-purple-100 text-purple-800',
      demo_scheduled: 'bg-indigo-100 text-indigo-800',
      quote_sent: 'bg-orange-100 text-orange-800',
      negotiating: 'bg-pink-100 text-pink-800',
      followup_waiting: 'bg-gray-100 text-gray-800',
      completed_success: 'bg-green-100 text-green-800',
      completed_rejected: 'bg-red-100 text-red-800',
      completed_postponed: 'bg-gray-100 text-gray-600',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadgeColor = (priority: string) => {
    const priorityColors: { [key: string]: string } = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800',
    };
    return priorityColors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">문의 관리</h1>
              <p className="text-gray-600 mt-2">
                고객 문의를 효율적으로 관리하세요
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* TODO: 알림 카운트 기능 복구 필요 */}
              {/* {newContactNotifications > 0 && (
                <div className="flex items-center bg-red-100 text-red-800 px-3 py-2 rounded-full">
                  <BellIcon className="h-5 w-5 mr-1" />
                  <span className="font-medium">
                    {newContactNotifications}개의 새 알림
                  </span>
                </div>
              )} */}
              <button
                onClick={fetchContacts}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                새로고침
              </button>
            </div>
          </div>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-600">전체 문의</div>
            <div className="text-2xl font-bold text-gray-900">
              {stats.total_contacts}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-600">새 문의</div>
            <div className="text-2xl font-bold text-blue-600">
              {stats.new_contacts}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-600">진행중</div>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.in_progress_contacts}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-600">완료</div>
            <div className="text-2xl font-bold text-green-600">
              {stats.completed_contacts}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-600">오늘</div>
            <div className="text-2xl font-bold text-purple-600">
              {stats.today_contacts}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-600">이번 주</div>
            <div className="text-2xl font-bold text-indigo-600">
              {stats.this_week_contacts}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-600">이번 달</div>
            <div className="text-2xl font-bold text-pink-600">
              {stats.this_month_contacts}
            </div>
          </div>
        </div>

        {/* 필터링 및 검색 */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                검색
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="이름, 회사명, 이메일 검색"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                상태
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">전체 상태</option>
                <option value="new">신규 접수</option>
                <option value="reviewing">검토중</option>
                <option value="in_progress">상담진행중</option>
                <option value="demo_scheduled">데모예정</option>
                <option value="quote_sent">견적발송</option>
                <option value="negotiating">계약협의</option>
                <option value="completed_success">완료(성사)</option>
                <option value="completed_rejected">완료(거절)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                우선순위
              </label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">전체 우선순위</option>
                <option value="low">낮음</option>
                <option value="medium">보통</option>
                <option value="high">높음</option>
                <option value="urgent">긴급</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                사업분야
              </label>
              <select
                value={businessTypeFilter}
                onChange={(e) => setBusinessTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">전체 분야</option>
                <option value="studyroom">독서실</option>
                <option value="academy">학원</option>
                <option value="gym">헬스장/피트니스</option>
                <option value="smallbusiness">소기업</option>
                <option value="other">기타</option>
              </select>
            </div>
          </div>
        </div>

        {/* 문의 목록 테이블 */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    고객정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사업분야
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    우선순위
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    접수일시
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contact.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {contact.company}
                        </div>
                        <div className="text-sm text-gray-500">
                          {contact.phone}
                        </div>
                        <div className="text-sm text-gray-500">
                          {contact.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contact.business_type_display}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(contact.status)}`}
                      >
                        {contact.status_display}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadgeColor(contact.priority)}`}
                      >
                        {contact.priority_display}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(contact.created_at).toLocaleDateString(
                          'ko-KR'
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(contact.created_at).toLocaleTimeString(
                          'ko-KR',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleContactClick(contact)}
                          className="text-blue-600 hover:text-blue-900"
                          title="상세보기"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="text-gray-600 hover:text-gray-900"
                          title="편집"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* 문의가 없을 때 */}
        {!loading && contacts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">조건에 맞는 문의가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 상세보기 모달 */}
      <ContactDetailModal
        contact={selectedContact}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedContact(null);
        }}
        onEdit={(contact) => {
          setShowDetailModal(false);
          setSelectedContact(contact);
          setShowEditModal(true);
        }}
      />

      {/* 편집 모달 */}
      <ContactEditModal
        contact={selectedContact}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedContact(null);
        }}
        onSave={() => {
          fetchContacts();
          fetchStats();
        }}
      />
    </div>
  );
};

export default ContactManagement;
